/**
 * Interactive Assessment & MCQ Quiz with immediate visual feedback, audio cues, and explanation.
 */
import { SVGObject, SVGObjectConfig } from '../objects/SVGObject';
import { Renderer } from '../core/Renderer';
import { QuizSchema } from '../parser/Schema';
import { AudioManager } from '../audio/AudioManager';
import { AssessmentApiService } from '../assessment/services/AssessmentApiService';
import { AuthService } from '../services/AuthService';

export class QuizObject extends SVGObject {
  private schema: QuizSchema;
  private audio: AudioManager;
  private selectedIndex: number = -1;
  private isAnswered: boolean = false;
  public onAnswer?: (correct: boolean) => void;

  constructor(config: SVGObjectConfig, schema: QuizSchema, audio: AudioManager) {
    super({ ...config, type: 'quiz', layer: 'ui' });
    this.schema = schema;
    this.audio = audio;
  }

  public render(): void {
    this.container.innerHTML = '';
    const w = 760;
    const h = 420;

    // Card background
    const bg = Renderer.createElement('rect');
    Renderer.setAttributes(bg, {
      x: -w / 2,
      y: -h / 2,
      width: w,
      height: h,
      rx: 16,
      ry: 16,
      fill: '#0f172a',
      stroke: '#38bdf8',
      'stroke-width': 2,
      filter: 'drop-shadow(0 20px 25px rgba(0,0,0,0.6))'
    });
    this.container.appendChild(bg);

    // Question Title
    const qTitle = Renderer.createElement('text');
    Renderer.setAttributes(qTitle, {
      x: 0,
      y: -h / 2 + 50,
      'font-size': 20,
      'font-family': 'Inter, sans-serif',
      'font-weight': '700',
      fill: '#f8fafc',
      'text-anchor': 'middle'
    });
    qTitle.textContent = this.schema.question;
    this.container.appendChild(qTitle);

    // 4 Options
    const optW = 680;
    const optH = 50;
    const startY = -h / 2 + 100;

    this.schema.options.forEach((opt, idx) => {
      const g = Renderer.createElement('g');
      const optY = startY + idx * 60;

      let fill = '#1e293b';
      let stroke = '#334155';

      if (this.isAnswered) {
        if (idx === this.schema.correctIndex) {
          fill = 'rgba(34, 197, 94, 0.2)';
          stroke = '#22c55e';
        } else if (idx === this.selectedIndex) {
          fill = 'rgba(239, 68, 68, 0.2)';
          stroke = '#ef4444';
        }
      }

      const rect = Renderer.createElement('rect');
      Renderer.setAttributes(rect, {
        x: -optW / 2,
        y: optY,
        width: optW,
        height: optH,
        rx: 10,
        ry: 10,
        fill,
        stroke,
        'stroke-width': 1.5,
        cursor: this.isAnswered ? 'default' : 'pointer'
      });

      const text = Renderer.createElement('text');
      Renderer.setAttributes(text, {
        x: -optW / 2 + 24,
        y: optY + optH / 2,
        'font-size': 16,
        'font-family': 'Inter, sans-serif',
        'font-weight': '500',
        fill: '#f8fafc',
        'dominant-baseline': 'central',
        cursor: this.isAnswered ? 'default' : 'pointer'
      });
      text.textContent = `${String.fromCharCode(65 + idx)}.  ${opt}`;

      if (!this.isAnswered) {
        g.onclick = () => this.handleSelect(idx);
      }

      g.appendChild(rect);
      g.appendChild(text);
      this.container.appendChild(g);
    });

    // Explanation if answered
    if (this.isAnswered) {
      const expText = Renderer.createElement('text');
      Renderer.setAttributes(expText, {
        x: 0,
        y: h / 2 - 35,
        'font-size': 14,
        'font-family': 'Inter, sans-serif',
        'font-weight': '600',
        fill: this.selectedIndex === this.schema.correctIndex ? '#4ade80' : '#f87171',
        'text-anchor': 'middle'
      });
      expText.textContent = this.schema.explanation;
      this.container.appendChild(expText);
    }
  }

  private handleSelect(idx: number): void {
    if (this.isAnswered) return;
    this.selectedIndex = idx;
    this.isAnswered = true;
    const isCorrect = idx === this.schema.correctIndex;

    this.audio.playSoundCue(isCorrect ? 'correct' : 'wrong');
    this.render();
    this.onAnswer?.(isCorrect);

    // Track In-Lesson Pop Quiz progress to LoopBack course-progresses
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const courseId = searchParams.get('courseId') || 'course-default';
      const pageId = searchParams.get('pageId') || 'page-default';
      const auth = AuthService.getInstance();
      const candidateId = auth.getUserId() || 'candidate-default';

      AssessmentApiService.getInstance().recordCourseProgress({
        candidateId,
        courseId,
        courseContentType: 'quiz',
        courseContentStatus: isCorrect ? 'Done' : 'Completed',
        obtainedPoints: isCorrect ? 1 : 0,
        totalPoints: 1,
        courseContentDetails: {
          quizQuestion: this.schema.question,
          selectedOption: idx,
          correctOption: this.schema.correctIndex,
          pageId
        }
      });

      // Notify parent host iframe (e.g. Website-CMS / ScormPlayer widget)
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          event: 'saveTrackingData',
          trackingData: {
            courseId,
            pageId,
            courseContentType: 'quiz',
            courseContentStatus: isCorrect ? 'Done' : 'Completed',
            score: isCorrect ? 1 : 0
          }
        }, '*');
      }
    } catch (err) {
      console.warn('[Quiz] Failed to sync pop quiz progress:', err);
    }
  }
}

